import {
  getFallbackList,
  isCDNFeatureFlagEnabled,
  useRequestTranslations,
} from '@procore/cdn-translations'
import type { I18njs, I18njsConfig } from '@procore/core-i18n-js'
import { createI18njs } from '@procore/core-i18n-js'
import * as R from 'ramda'
import React, { type PropsWithChildren } from 'react'
import deDE from '../_locales/de-DE.json'
import enAU from '../_locales/en-AU.json'
import enCA from '../_locales/en-CA.json'
import enGB from '../_locales/en-GB.json'
import en from '../_locales/en.json'
import esES from '../_locales/es-ES.json'
import es from '../_locales/es.json'
import frCA from '../_locales/fr-CA.json'
import frFR from '../_locales/fr-FR.json'
import isIS from '../_locales/is-IS.json'
import itIT from '../_locales/it-IT.json'
import jaJP from '../_locales/ja-JP.json'
import nbNO from '../_locales/nb-NO.json'
import plPL from '../_locales/pl-PL.json'
import pseudo from '../_locales/pseudo.json'
import ptBR from '../_locales/pt-BR.json'
import ptPT from '../_locales/pt-PT.json'
import thTH from '../_locales/th-TH.json'
import zhSG from '../_locales/zh-SG.json'
import zhTW from '../_locales/zh-TW.json'
import { OVERWRITE_LOCALE_MAP } from './getI18nProviderLocale'

type AssertExtends<T, U extends T> = U

// Incase of error, Make sure I18njsConfig['locale'] has the same locales as Locale
type _ = AssertExtends<I18njsConfig['locale'], Locale>

type I18nConfig = Omit<I18njsConfig, 'I18n'>

type I18nWithCDNFlag = I18njs & {
  enableCDN?: boolean
}

/**
 * @deprecated this type will no longer be supported from core-i18n-js,
 * to have a type safe Locale use TmsSupportedLocale or AllLocales from [Globalization toolkit](https://github.com/procore/globalization-toolkit)
 * @deprecatedSince 12.10.0
 */
export type Locale =
  | 'de-DE'
  | 'en-AE'
  | 'en-AU'
  | 'en-CA'
  | 'en-GB'
  | 'en-SG'
  | 'en'
  | 'es-ES'
  | 'es'
  | 'fr-CA'
  | 'fr-FR'
  | 'is-IS'
  | 'ja-JP'
  | 'pt-BR'
  | 'th-TH'
  | 'zh-SG'
  | 'pseudo'
  | 'pl-PL'
  | 'nb-NO'
  | 'zh-TW'
  | 'it-IT'
  | 'pt-PT'

/* Set up core translations */
export const coreTranslations = {
  'de-DE': deDE,
  en,
  'en-AU': enAU,
  'en-CA': enCA,
  'en-GB': enGB,
  es,
  'es-ES': esES,
  'fr-CA': frCA,
  'fr-FR': frFR,
  'is-IS': isIS,
  'ja-JP': jaJP,
  pseudo,
  'pt-BR': ptBR,
  'th-TH': thTH,
  'zh-SG': zhSG,
  'pl-PL': plPL,
  'nb-NO': nbNO,
  'zh-TW': zhTW,
  'it-IT': itIT,
  'pt-PT': ptPT,
} as const

const FALLBACK_LOCALE_MAP: Record<string, Locale> = {
  'es-ES': 'es',
  'fr-FR': 'fr-CA',
  ...OVERWRITE_LOCALE_MAP,
}

function getTranslationFallbacks(locale: string): Locale[] {
  if (FALLBACK_LOCALE_MAP[locale]) {
    return [FALLBACK_LOCALE_MAP[locale], 'en']
  }

  return ['en']
}

function setTranslationFallbacks(
  I18n: I18nWithCDNFlag,
  isCDNEnabled: boolean | undefined
) {
  if (isCDNEnabled === true) {
    I18n.locales[I18n.locale] = function () {
      return getFallbackList(I18n.locale)
    }
  } else {
    I18n.locales[I18n.locale] = function () {
      const fallbacks = getTranslationFallbacks(I18n.locale).filter(
        (locale) => locale !== I18n.locale
      )
      return [I18n.locale, ...fallbacks]
    }
  }

  return I18n
}

type UseI18n = I18nConfig & {
  enableCDN?: boolean
}

/**
 * Hook to create I18n object.
 * @param {UseI18n} props - The I18n configuration.
 * @param {string} props.locale - The locale to use.
 * @param {Record<string, string>} props.translations - The translations to use.
 * @param {boolean} props.enableCDN - Whether to pull core-react translations from the CDN or not,   by default it follows the value passed down through the parent I18nProvider.
 * @returns {I18nWithCDNFlag} The I18n object.
 */
export function useI18n({
  locale,
  translations: _translations = {},
  enableCDN,
}: UseI18n): I18nWithCDNFlag {
  const i18nContext: I18nWithCDNFlag | undefined = React.useContext(I18nContext)

  const isCDNEnabled = isCDNFeatureFlagEnabled(i18nContext, enableCDN)

  const coreCDNTranslations = useRequestTranslations(
    {
      locale: locale ?? 'en',
      type: 'file',
      absolute_file_path: (locale) =>
        `core/packages/core-react/src/_locales/${locale}.json`,
    },
    { en, pseudo },
    {
      oldTranslations: coreTranslations,
      enableCDN: isCDNEnabled,
    }
  )

  const translations = R.mergeDeepLeft(
    _translations,
    coreCDNTranslations.translations
  )

  const I18n = createI18njs({
    locale,
    translations,
  })

  const I18nWithFallbacks = setTranslationFallbacks(I18n, isCDNEnabled)

  I18nWithFallbacks.enableCDN = isCDNEnabled

  return I18nWithFallbacks
}

export const I18nContext: React.Context<I18nWithCDNFlag> = React.createContext(
  createI18njs({
    locale: 'en',
    translations: { en: coreTranslations.en },
  })
)

/**
 * Provider to wrap the application with the I18n context.
 * @param {PropsWithChildren<UseI18n>} props - The I18n configuration.
 * @param {React.ReactNode} props.children - The children to render.
 * @param {UseI18n} props.locale - The locale to use.
 * @param {Record<string, string>} props.translations - The translations to use.
 * @param {boolean} props.enableCDN - Whether to pull core-react translations from the CDN or not, by default it follows the value passed down through the parent I18nProvider.
 * @returns {React.ReactNode} The I18n context instance.
 */
export function I18nProvider({
  children,
  ...props
}: PropsWithChildren<UseI18n>) {
  return (
    <I18nContext.Provider value={useI18n(props)}>
      {children}
    </I18nContext.Provider>
  )
}

// Base type of user provider translations type
type DeepStringRecord = {
  [key: string]: string | DeepStringRecord
}

type PluralizationForm = {
  zero?: string
  one: string
  other: string
}

// Type to get nested key paths with dot notation
type DotPrefix<T extends string> = T extends '' ? '' : `.${T}`
type DotNestedKeys<T> = (
  T extends string
    ? ''
    : T extends DeepStringRecord
    ? {
        [K in keyof T]: K extends string
          ? T[K] extends PluralizationForm
            ? `${K}`
            : T[K] extends DeepStringRecord
            ? `${K}${DotPrefix<DotNestedKeys<T[K]>>}`
            : `${K}`
          : never
      }[keyof T]
    : ''
) extends infer D
  ? Extract<D, string>
  : never

// Type to get the literal string type from a path
type StringLiteralPathValue<
  T,
  P extends string
> = P extends `${infer K}.${infer Rest}`
  ? K extends keyof T
    ? T[K] extends PluralizationForm
      ? T[K]
      : T[K] extends DeepStringRecord
      ? StringLiteralPathValue<T[K], Rest>
      : never
    : never
  : P extends keyof T
  ? T[P] extends PluralizationForm
    ? T[P]
    : T[P] extends string
    ? T[P]
    : never
  : never

// Check if a string has any interpolation variables
type HasInterpolation<T extends string> =
  T extends `${string}%{${string}}${string}` ? true : false

// Extract interpolation variables from a string
type ExtractInterpolationVars<T extends string> =
  T extends `${infer _Before}%{${infer Param}}${infer After}`
    ? Param | ExtractInterpolationVars<After>
    : never

// always allowed values in the interpolation object (remove [key: string]: any in favor of our strong types)
type ExplicitTranslateOptions = {
  [K in keyof I18n.TranslateOptions as string extends K
    ? never
    : K]: I18n.TranslateOptions[K]
}

type InterpolationParams<T extends string> = HasInterpolation<T> extends true
  ? {
      [K in ExtractInterpolationVars<T>]: string
    } & {
      [K in keyof ExplicitTranslateOptions]?: ExplicitTranslateOptions[K]
    }
  : never

type PluralizationParams<T extends PluralizationForm> = {
  count: number
} & (
  | (T['zero'] extends string
      ? { count: 0 } & { [K in ExtractInterpolationVars<T['zero']>]: string }
      : {})
  | (T['one'] extends string
      ? { count: 1 } & { [K in ExtractInterpolationVars<T['one']>]: string }
      : {})
  | (T['other'] extends string
      ? { count: number } & {
          [K in ExtractInterpolationVars<T['other']>]: string
        }
      : {})
) & {
    [K in keyof ExplicitTranslateOptions]?: ExplicitTranslateOptions[K]
  }

type InterpolationOrPluralizationParams<T> = T extends PluralizationForm
  ? [PluralizationParams<T>]
  : T extends string
  ? InterpolationParams<T> extends never
    ? [ExplicitTranslateOptions?]
    : [InterpolationParams<T>]
  : [ExplicitTranslateOptions?]

type TypedTranslate<Translations> = <P extends DotNestedKeys<Translations>>(
  path: P,
  ...args: InterpolationOrPluralizationParams<
    StringLiteralPathValue<Translations, P>
  >
) => StringLiteralPathValue<Translations, P>

type I18njsWithTypedTranslate<Translations> = Omit<
  I18nWithCDNFlag,
  't' | 'translate'
> & {
  t: TypedTranslate<Translations>
  translate: TypedTranslate<Translations>
}

export function useI18nContext<
  Translations extends DeepStringRecord | undefined = undefined
>(): Translations extends undefined
  ? I18nWithCDNFlag
  : I18njsWithTypedTranslate<NonNullable<Translations>>
// overload for when no generic is provided
export function useI18nContext(): I18nWithCDNFlag

/**
 * Hook to access the I18n context.
 *
 * @template Translations - An optional generic to provide type safety for translations.
 * If specified, it enables typed access to the `t` and `translate` methods of the I18n instance.
 * These methods will have type safe access to both the tranlsation scope as well as the interpolation variables.
 * If not specified, the default I18nWithCDN instance is returned.
 *
 * @returns {I18nWithCDNFlag | I18njsWithTypedTranslate<Translations>} The I18n context instance,
 * either with or without typed translations based on the provided generic.
 */
export function useI18nContext<
  Translations extends DeepStringRecord | undefined = undefined
>() {
  const ctx = React.useContext(I18nContext)

  if (!ctx) {
    // TODO investigate a way to do warnings that won't annoy people
    // we probably need to introduce a build system to have nice dev warnings
    // console.warn()
  }

  // Cast to typing if user has provided a type
  return ctx as Translations extends undefined
    ? I18nWithCDNFlag
    : I18njsWithTypedTranslate<NonNullable<Translations>>
}
